import { ReactNode, useState, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, MessageSquare, Clock, Share2,
  LogOut, ChevronLeft, ChevronRight, ChevronDown, Bell, Menu, X,
  Users, Shield, SlidersHorizontal, BarChart3, Wrench, Star,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface LayoutProps {
  children: ReactNode;
}

interface NavItem {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  permission?: string;
}

interface NavGroup {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  children: NavItem[];
  permission?: string;
}

type NavEntry = NavItem | NavGroup;

function isGroup(entry: NavEntry): entry is NavGroup {
  return 'children' in entry;
}

const ALL_NAV_ITEMS: NavEntry[] = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', permission: 'dashboard.ver' },
  { to: '/chat', icon: MessageSquare, label: 'Agente', permission: 'agente.ver' },
  { to: '/history', icon: Clock, label: 'Historial', permission: 'historial.ver' },
  { to: '/shared', icon: Share2, label: 'Compartidos', permission: 'compartidos.ver' },
  { to: '/ratings', icon: Star, label: 'Calificaciones', permission: 'calificaciones.ver' },
  {
    label: 'Administración',
    icon: SlidersHorizontal,
    children: [
      { to: '/admin/dashboard', icon: BarChart3, label: 'Panel Admin', permission: 'admin_dashboard.ver' },
      { to: '/admin/ratings', icon: Star, label: 'Feedback', permission: 'feedback.ver' },
      { to: '/admin/users', icon: Users, label: 'Usuarios', permission: 'usuarios.ver' },
      { to: '/admin/roles', icon: Shield, label: 'Roles', permission: 'roles.ver' },
      { to: '/admin/config', icon: Wrench, label: 'Configuración', permission: 'admin_config.ver' },
    ],
  },
];

const PAGE_TITLES: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/chat': 'Agente de Análisis',
  '/history': 'Historial',
  '/shared': 'Análisis Compartidos',
  '/ratings': 'Mis Calificaciones',
  '/settings': 'Configuración',
  '/admin/users': 'Gestión de Usuarios',
  '/admin/roles': 'Gestión de Roles',
  '/admin/config': 'Configuración de Plataforma',
  '/admin/dashboard': 'Panel Administrativo',
  '/admin/ratings': 'Calificaciones y Feedback',
};

export default function Layout({ children }: LayoutProps) {
  const { user, logout, hasPermission } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [adminOpen, setAdminOpen] = useState(
    location.pathname.startsWith('/admin')
  );

  const navItems = useMemo(() => {
    return ALL_NAV_ITEMS.reduce<NavEntry[]>((acc, entry) => {
      if (isGroup(entry)) {
        const visibleChildren = entry.children.filter(
          child => !child.permission || hasPermission(child.permission),
        );
        if (visibleChildren.length > 0) {
          acc.push({ ...entry, children: visibleChildren });
        }
      } else {
        if (!entry.permission || hasPermission(entry.permission)) {
          acc.push(entry);
        }
      }
      return acc;
    }, []);
  }, [hasPermission]);

  const pageTitle = PAGE_TITLES[location.pathname] || 'Apoyo Financiero';

  const isAdminActive = location.pathname.startsWith('/admin');

  function renderNavItem(item: NavItem, opts?: { onClick?: () => void; isCollapsed?: boolean }) {
    const isCollapsed = opts?.isCollapsed ?? collapsed;
    return (
      <NavLink
        key={item.to}
        to={item.to}
        onClick={opts?.onClick}
        className={({ isActive }) =>
          `nav-item ${isActive ? 'active' : ''} ${isCollapsed ? 'justify-center' : ''}`
        }
        title={isCollapsed ? item.label : undefined}
      >
        <item.icon className="w-5 h-5 flex-shrink-0" />
        {!isCollapsed && <span>{item.label}</span>}
      </NavLink>
    );
  }

  function renderDesktopNav() {
    return navItems.map((entry, idx) => {
      if (!isGroup(entry)) {
        return renderNavItem(entry);
      }

      if (collapsed) {
        return entry.children.map(child => renderNavItem(child));
      }

      return (
        <div key={idx}>
          <button
            onClick={() => setAdminOpen(!adminOpen)}
            className={`nav-item w-full ${isAdminActive ? 'active' : ''}`}
          >
            <entry.icon className="w-5 h-5 flex-shrink-0" />
            <span className="flex-1 text-left">{entry.label}</span>
            <ChevronDown
              className={`w-4 h-4 transition-transform ${adminOpen ? '' : '-rotate-90'}`}
            />
          </button>
          <AnimatePresence initial={false}>
            {adminOpen && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: 'auto' }}
                exit={{ height: 0 }}
                className="overflow-hidden"
              >
                <div className="pl-4 space-y-0.5 mt-0.5">
                  {entry.children.map(child => renderNavItem(child))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    });
  }

  function renderMobileNav() {
    return navItems.map((entry, idx) => {
      if (!isGroup(entry)) {
        return renderNavItem(entry, { onClick: () => setMobileOpen(false), isCollapsed: false });
      }
      return (
        <div key={idx}>
          <button
            onClick={() => setAdminOpen(!adminOpen)}
            className={`nav-item w-full ${isAdminActive ? 'active' : ''}`}
          >
            <entry.icon className="w-5 h-5" />
            <span className="flex-1 text-left">{entry.label}</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${adminOpen ? '' : '-rotate-90'}`} />
          </button>
          {adminOpen && (
            <div className="pl-4 space-y-0.5 mt-0.5">
              {entry.children.map(child =>
                renderNavItem(child, { onClick: () => setMobileOpen(false), isCollapsed: false })
              )}
            </div>
          )}
        </div>
      );
    });
  }

  return (
    <div className="min-h-screen flex bg-[#F1F5F9]">
      {/* Sidebar - Desktop */}
      <aside
        className={`hidden lg:flex flex-col fixed top-0 left-0 h-screen z-30 transition-all duration-300 ${
          collapsed ? 'w-[68px]' : 'w-60'
        }`}
        style={{ backgroundColor: '#0F1729' }}
      >
        {/* Brand */}
        <div className={`flex items-center h-16 border-b border-white/5 ${collapsed ? 'justify-center px-2' : 'px-4'}`}>
          {collapsed ? (
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#EDAA00' }}>
              <span className="text-white font-bold text-sm">AF</span>
            </div>
          ) : (
            <img
              src="/assets/logo-uniandes-vaf.png"
              alt="Universidad de los Andes - VAF"
              className="h-8 object-contain brightness-0 invert"
            />
          )}
        </div>

        {/* Nav Items */}
        <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
          {renderDesktopNav()}
        </nav>

        {/* Bottom */}
        <div className="border-t border-white/5 py-3 px-2 space-y-1">
          <button onClick={logout} className={`nav-item w-full ${collapsed ? 'justify-center' : ''}`} title={collapsed ? 'Cerrar sesión' : undefined}>
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span>Cerrar sesión</span>}
          </button>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={`nav-item w-full ${collapsed ? 'justify-center' : ''}`}
          >
            {collapsed ? <ChevronRight className="w-5 h-5" /> : <><ChevronLeft className="w-5 h-5 flex-shrink-0" /><span>Colapsar</span></>}
          </button>
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
              transition={{ type: 'tween', duration: 0.25 }}
              className="fixed top-0 left-0 h-screen w-64 z-50 flex flex-col lg:hidden"
              style={{ backgroundColor: '#0F1729' }}
            >
              <div className="flex items-center justify-between h-16 px-4 border-b border-white/5">
                <img
                  src="/assets/logo-uniandes-vaf.png"
                  alt="Universidad de los Andes - VAF"
                  className="h-8 object-contain brightness-0 invert"
                />
                <button onClick={() => setMobileOpen(false)} className="p-1 rounded-lg hover:bg-white/10">
                  <X className="w-5 h-5 text-white/60" />
                </button>
              </div>
              <nav className="flex-1 py-4 px-2 space-y-1">
                {renderMobileNav()}
              </nav>
              <div className="border-t border-white/5 py-3 px-2 space-y-1">
                <button onClick={() => { logout(); setMobileOpen(false); }} className="nav-item w-full">
                  <LogOut className="w-5 h-5" /> <span>Cerrar sesión</span>
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main content area */}
      <div className={`flex-1 flex flex-col min-h-screen transition-all duration-300 ${collapsed ? 'lg:ml-[68px]' : 'lg:ml-60'}`}>
        {/* Top bar */}
        <header className="sticky top-0 z-20 h-16 bg-white border-b border-gray-200/80 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-gray-100">
              <Menu className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">{pageTitle}</h1>
          </div>

          <div className="flex items-center gap-2">
            <button className="p-2 rounded-lg hover:bg-gray-100 relative">
              <Bell className="w-5 h-5 text-gray-500" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500" />
            </button>

            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white" style={{ backgroundColor: '#1B2A4A' }}>
                  {user?.avatar || '??'}
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-medium text-gray-900 leading-tight">{user?.name}</p>
                  <p className="text-[10px] text-gray-500">{user?.role}</p>
                </div>
              </button>

              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}
                    className="absolute right-0 top-12 w-56 bg-white border border-gray-200 rounded-xl shadow-lg py-2 z-50"
                  >
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                      <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-blue-50 text-blue-700">
                        {user?.role}
                      </span>
                    </div>
                    <div className="pt-1">
                      <button onClick={logout} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                        <LogOut className="w-4 h-4" /> Cerrar sesión
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
}
